import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * MoneyInput — Brazilian-style natural money input.
 *
 * User types naturally (e.g. "5000" for R$ 5.000 or "5000,50" for R$ 5.000,50).
 * On blur, the value is auto-formatted with thousand separators and 2 decimals.
 * Value exposed to parent is a plain Number (BRL), same API as before.
 *
 * Examples of accepted typing:
 *   "150"      → number 150       → on blur "150,00"
 *   "1500"     → number 1500      → on blur "1.500,00"
 *   "1500,5"   → number 1500.5    → on blur "1.500,50"
 *   "1.500,50" → number 1500.5    → on blur "1.500,50"
 *   ""         → number 0         → on blur ""
 */

function parseBR(s) {
  if (s === null || s === undefined || s === "") return 0;
  if (typeof s === "number") return isNaN(s) ? 0 : s;
  let cleaned = String(s).replace(/[^\d,.\-]/g, "").trim();
  if (!cleaned || cleaned === "-" || cleaned === "," || cleaned === ".") return 0;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      // BR: 1.500,50 → drop dots, replace comma
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // US: 1,500.50 → drop commas
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (lastComma > -1) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (dotCount > 1) {
    // Multiple dots, no comma → BR thousand separators (5.800.000 = 5800000)
    cleaned = cleaned.replace(/\./g, "");
  }
  // Single dot or plain digits → natural JS parse (dot as decimal)
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

function formatBR(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "";
  return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const MoneyInput = forwardRef(function MoneyInput(
  { value, onChange, placeholder = "0,00", "data-testid": testId, className = "", disabled, ...rest },
  ref
) {
  const initial = () => {
    const num = Number(value || 0);
    return num === 0 ? "" : formatBR(num);
  };
  const [str, setStr] = useState(initial);
  const editingRef = useRef(false);

  // Sync from parent when NOT actively typing (external change, e.g. bidirectional calc)
  useEffect(() => {
    if (editingRef.current) return;
    const numFromStr = parseBR(str);
    const numExt = Number(value || 0);
    if (Math.abs(numFromStr - numExt) > 0.005) {
      setStr(numExt === 0 ? "" : formatBR(numExt));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    editingRef.current = true;
    const cleaned = e.target.value.replace(/[^\d,.\-]/g, "");
    setStr(cleaned);
    onChange?.(parseBR(cleaned));
  };
  const handleFocus = () => { editingRef.current = true; };
  const handleBlur = () => {
    editingRef.current = false;
    const num = parseBR(str);
    setStr(num === 0 ? "" : formatBR(num));
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={str}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testId}
      className={className}
      {...rest}
    />
  );
});
